import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "onOpenChange", "open", "shrinkOnHide"];
import { PureComponent } from 'react';
import styled from 'foundations-theming/utils';
import { closest } from '../../utils/dom';
import { Controllable } from '../Controllable';
import { syntheticEvent } from '../../utils/SyntheticEvent';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { KeyboardModeContext } from '../../../components/AppProvider/KeyboardModeProvider';
import { toPx } from '../../../utils/toPx';
import memoizeOne from 'react-utils/memoizeOne';
import { jsx as _jsx } from "react/jsx-runtime";
const getClosestTable = el => closest(el, 'TABLE');
const getClosestTableRow = el => closest(el, 'TR');
const getPositionStyle = (top, left) => {
  if (top === null || left === null) return null;
  return {
    top: toPx(top),
    left: toPx(left)
  };
};
const bindEventListeners = instance => {
  // Add event listeners to the row and table this component lives in
  const table = getClosestTable(instance._el);
  const tr = getClosestTableRow(instance._el);
  if (!tr || !table) return;
  table.addEventListener('mouseleave', instance.handleRowOrTableMouseLeave);
  tr.addEventListener('mouseleave', instance.handleRowOrTableMouseLeave);
  tr.addEventListener('mouseenter', instance.handleRowMouseEnter);
  tr.addEventListener('focus', instance.handleRowFocus, true);
  tr.addEventListener('blur', instance.handleRowBlur, true);

  // If the user clicks a control with a mouse, we want to ignore the focus on that control (#3054)
  tr.addEventListener('mouseup', instance.handleRowBlur);
};
const unbindEventListeners = instance => {
  // Clean up any event handlers we've added to other elements
  const table = getClosestTable(instance._el);
  const tr = getClosestTableRow(instance._el);
  if (!tr || !table) return;
  table.removeEventListener('mouseleave', instance.handleRowOrTableMouseLeave);
  tr.removeEventListener('mouseenter', instance.handleRowMouseEnter);
  tr.removeEventListener('mouseleave', instance.handleRowOrTableMouseLeave);
  tr.removeEventListener('focus', instance.handleRowFocus, true);
  tr.removeEventListener('blur', instance.handleRowBlur, true);
  tr.removeEventListener('mouseup', instance.handleRowBlur);
};
const StyledSpan = styled.span.withConfig({
  displayName: "TableHoverContent__StyledSpan",
  componentId: "mkbho3-0"
})(({
  $open,
  $shrinkOnHide,
  $tokenProps,
  $top,
  $left
}) => {
  const $cssStyles = useMapTokenPropsToCSS($tokenProps);
  return Object.assign({
    opacity: $open ? 1 : 0,
    position: $shrinkOnHide && !$open ? 'absolute' : 'unset',
    insetBlockStart: $top,
    insetInlineStart: $left,
    whiteSpace: 'nowrap'
  }, $cssStyles);
});
class TableHoverContentComponent extends PureComponent {
  constructor(props) {
    super(props);
    this.checkForOpenDropdown = () => {
      if (!this._el) return;
      const rowHasOpenDropdown = !!this._el.querySelector('[data-dropdown-open="true"]');
      if (this.state.rowHasOpenDropdown !== rowHasOpenDropdown) {
        this.setState({
          rowHasOpenDropdown
        });
      }
    };
    this.handleRowMouseEnter = () => {
      this.setState({
        rowHasMouse: true
      });
    };
    this.handleRowOrTableMouseLeave = () => {
      // Sometimes mouseleave is triggered on the <table> but not the <tr>
      if (this.state.rowHasMouse) {
        this.setState({
          rowHasMouse: false
        });
      }
    };
    this.handleRowFocus = () => {
      const {
        isKeyboardMode
      } = this.context;
      // Handle focus events only if the user is interacting using the keyboard (#8548)
      this.setState(() => isKeyboardMode ? {
        rowHasFocus: true
      } : null);
    };
    this.handleRowBlur = () => {
      this.setState({
        rowHasFocus: false
      });
    };
    this.state = {
      top: null,
      left: null,
      rowHasFocus: false,
      rowHasMouse: false,
      rowHasOpenDropdown: false
    };
    this._getPositionStyle = memoizeOne(getPositionStyle);
  }
  componentDidMount() {
    requestAnimationFrame(() => {
      bindEventListeners(this);
      this.checkForOpenDropdown();
      if (this._el) {
        this._observer = new MutationObserver(this.checkForOpenDropdown);
        this._observer.observe(this._el, {
          attributes: true,
          childList: true,
          subtree: true
        });
      }
    });
  }
  componentDidUpdate(prevProps, prevState) {
    // In shrinkOnHide mode, compute our absolute position before hiding (#2412)
    if (this.props.shrinkOnHide && prevProps.open && !this.props.open) {
      const {
        top,
        left
      } = this.computeAbsolutePosition();
      if (top !== prevState.top || left !== prevState.left) {
        this.setState({
          top,
          left
        });
      }
    }
    const {
      onOpenChange
    } = this.props;
    const {
      rowHasFocus,
      rowHasMouse,
      rowHasOpenDropdown
    } = this.state;
    // These state checks prevent infinite update loops when `open` is controlled.
    if (rowHasFocus !== prevState.rowHasFocus || rowHasMouse !== prevState.rowHasMouse || rowHasOpenDropdown !== prevState.rowHasOpenDropdown) {
      const shouldBeOpen = rowHasFocus || rowHasMouse || rowHasOpenDropdown;
      if (onOpenChange) {
        onOpenChange(syntheticEvent(shouldBeOpen));
      }
    }
  }
  componentWillUnmount() {
    unbindEventListeners(this);
    if (this._observer) this._observer.disconnect();
  }
  computeAbsolutePosition() {
    // If we're visible but have shrinkOnHide, compute our absolute position.

    const originalDisplay = this._el.style.display;
    const originalPosition = this._el.style.position;
    const originalTop = this._el.style.top;
    const originalLeft = this._el.style.left;
    this._el.style.display = 'block';
    const {
      left,
      top
    } = this._el.getBoundingClientRect();
    this._el.style.display = originalDisplay;

    // This measurement tells us how far position: absolute moves us,
    // with top and left set to 0.
    this._el.style.position = 'absolute';
    this._el.style.top = '0';
    this._el.style.left = '0';
    const {
      left: absLeft,
      top: absTop
    } = this._el.getBoundingClientRect();
    this._el.style.position = originalPosition;
    this._el.style.top = originalTop;
    this._el.style.left = originalLeft;

    // We take the difference between the two measurements as the appropriate
    // top and left values to apply while hidden.
    return {
      left: left - absLeft,
      top: top - absTop
    };
  }
  render() {
    const _this$props = this.props,
      {
        children,
        open,
        shrinkOnHide
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const {
      top,
      left
    } = this.state;
    const {
      tokenProps,
      domProps
    } = filterProps(rest);
    const positionStyle = open || !shrinkOnHide ? null : this._getPositionStyle(top, left);
    return /*#__PURE__*/_jsx(StyledSpan, Object.assign({
      ref: ref => {
        this._el = ref;
      },
      $open: open,
      $shrinkOnHide: shrinkOnHide,
      $tokenProps: tokenProps,
      $top: positionStyle === null || positionStyle === void 0 ? void 0 : positionStyle.top,
      $left: positionStyle === null || positionStyle === void 0 ? void 0 : positionStyle.left
    }, domProps, {
      children: children
    }));
  }
}
TableHoverContentComponent.defaultProps = {
  open: false,
  shrinkOnHide: false
};
TableHoverContentComponent.contextType = KeyboardModeContext;
export const TableHoverContent = Controllable(TableHoverContentComponent, ['open']);